import { useState } from 'react';
import { MediaCard } from './MediaCard';
import { MediaType } from '../types/media.types';
import { useUserMedia, useUploadMedia, useDeleteMedia, useStorageInfo } from '../hooks/useMediaQuery';
import { DeleteConfirmationDialog } from '@/components/ui/DeleteConfirmationDialog';
import Modal from '@/components/Modal';
import toast from 'react-hot-toast';

const TABS: MediaType[] = ['image', 'video', 'audio', 'document'];

const SIZE_LIMITS_MB: Record<MediaType, number> = {
  image: 5,
  video: 16,
  audio: 16,
  document: 100,
};

type MediaTypeProps = {
  excludedExtentions?: string[];
  selectedMediaType: MediaType | undefined;
  setSelectedMedia:any;
  onClose?: React.Dispatch<React.SetStateAction<boolean>>;
};

function convertToPng(file: File): Promise<{ blob: Blob; name: string; size: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx?.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);

      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error("Conversion failed"));
          const outputName = file.name.replace(/\.[^.]+$/, "") + ".png";
          resolve({ blob, name: outputName, size: blob.size });
        },
        "image/png"
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };

    img.src = url;
  });
}

const NOT_TRIGGER_CONVERSION_TYPES = ['image/png', 'image/jpeg'];

export const MediaLibrary = ({ selectedMediaType, setSelectedMedia, onClose, excludedExtentions }: MediaTypeProps) => {
  const [activeTab, setActiveTab] = useState<MediaType>(selectedMediaType || 'image');

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [mediaToDelete, setMediaToDelete] = useState<{ id: string; name: string } | null>(null);

  // PNG conversion prompt state
  const [convertPromptOpen, setConvertPromptOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<{ file: File; inputRef: HTMLInputElement } | null>(null);
  const [isConverting, setIsConverting] = useState(false);

  // API hooks
  const { data: mediaData, isLoading: isLoadingMedia } = useUserMedia(activeTab);
  const { data: storageData } = useStorageInfo();
  const uploadMediaMutation = useUploadMedia();
  const deleteMediaMutation = useDeleteMedia();

  const items = mediaData?.media || [];
  const storage = storageData?.storage;

  const isFileSizeValid = (file: File, tab: MediaType): boolean => {
    const limitBytes = SIZE_LIMITS_MB[tab] * 1024 * 1024;
    return file.size <= limitBytes;
  };

  const uploadFile = async (file: File) => {
    try {
      await uploadMediaMutation.mutateAsync(file);
      toast.success('File uploaded successfully!');
    } catch (error: any) {
      console.error('Upload failed:', error);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 1. Check size limit first
    if (!isFileSizeValid(file, activeTab)) {
      const limitMB = SIZE_LIMITS_MB[activeTab];
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
      toast.error(`File too large (${fileSizeMB}MB). Max allowed for ${activeTab}s is ${limitMB}MB.`);
      e.target.value = '';
      return;
    }

    // 2. Check extension support for non-image tabs
    if (activeTab !== 'image' && !hasExtentionSupported(file)) {
      toast.error(`File type "${file.type}" is not supported. Please select a different file.`);
      e.target.value = '';
      return;
    }

    // 3. If image but not PNG/JPEG, prompt for conversion
    if (activeTab === 'image' && !NOT_TRIGGER_CONVERSION_TYPES.includes(file.type)) {
      setPendingFile({ file, inputRef: e.target });
      setConvertPromptOpen(true);
      return;
    }

    await uploadFile(file);
    if (e.target) e.target.value = '';
  };

  const hasExtentionSupported = (file: File): boolean => {
    switch (activeTab) {
      case 'video':
        return ['video/mp4', 'video/3gpp'].includes(file.type);
      case 'audio':
        return ['audio/mp4', 'audio/aac', 'audio/amr', 'audio/mpeg', 'audio/ogg'].includes(file.type);
      case 'document':
        return [
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'text/plain',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/vnd.ms-powerpoint',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        ].includes(file.type);
      default:
        return false;
    }
  };

  const handleConvertAndUpload = async () => {
    if (!pendingFile) return;
    setIsConverting(true);
    try {
      const { blob, name, size } = await convertToPng(pendingFile.file);

      // Check size of converted PNG as well
      const limitMB = SIZE_LIMITS_MB['image'];
      const convertedSizeMB = size / (1024 * 1024);
      if (convertedSizeMB > limitMB) {
        toast.error(`Converted PNG is too large (${convertedSizeMB.toFixed(1)}MB). Max allowed for images is ${limitMB}MB.`);
        return;
      }

      const convertedFile = new File([blob], name, { type: 'image/png' });
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await uploadFile(convertedFile);
      toast.success('Image converted to PNG and uploaded!');
    } catch (error) {
      toast.error('Conversion failed. Please try again.');
      console.error('Conversion failed:', error);
    } finally {
      setIsConverting(false);
      setConvertPromptOpen(false);
      pendingFile.inputRef.value = '';
      setPendingFile(null);
    }
  };

  const handleCancelConvert = () => {
    if (pendingFile) {
      pendingFile.inputRef.value = '';
    }
    setPendingFile(null);
    setConvertPromptOpen(false);
  };

  const handleDeleteMedia = (mediaId: string) => {
    const media = items.find(item => item.id === mediaId);
    if (!media) return;
    setMediaToDelete({ id: mediaId, name: media.originalName });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!mediaToDelete) return;
    try {
      await deleteMediaMutation.mutateAsync(mediaToDelete.id);
      toast.success('File deleted successfully!');
      setDeleteDialogOpen(false);
      setMediaToDelete(null);
    } catch (error: any) {
      console.error('Delete failed:', error);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setMediaToDelete(null);
  };

  const getAcceptString = (type: MediaType): string => {
    switch (type) {
      case 'image': return 'image/*';
      case 'video': return 'video/*';
      case 'audio': return 'audio/*';
      case 'document': return '.pdf,.doc,.docx,.txt,.xlsx,.xls,.ppt,.pptx';
      default: return '*/*';
    }
  };
  const pendingFileExt = pendingFile
  ? pendingFile.file.name.split('.').pop()?.toUpperCase()
  : '';

return (
  <Modal
    isOpen={true}
    onClose={() => onClose?.(false)}
    title={
      <div>
        <div className="text-lg font-semibold">Media Library</div>
        <div className="text-sm text-gray-500 dark:text-gray-400 font-normal">Manage and select your media files</div>
      </div>
    }
    size="4xl"
    closeOnBackdropClick={true}
    actions={[
      {
        label: uploadMediaMutation.isPending ? 'Uploading...' : 'Upload Media',
        color: "primary",
        disabled: uploadMediaMutation.isPending,
        onClick: () => document.getElementById('file-upload-input')?.click()
      }
    ]}
    className="p-0"
    bodyClassName="p-0"
  >
    <input
      id="file-upload-input"
      type="file"
      accept={getAcceptString(activeTab)}
      className="hidden"
      onChange={handleUpload}
      disabled={uploadMediaMutation.isPending}
    />
    <div className="w-full h-full flex flex-col p-6 bg-white dark:bg-[#1a1a1a]">
      {/* Storage Info */}
      {storage && (
        <div className="mb-6 flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-gray-600 dark:text-gray-300">Storage:</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {storage.usedMB.toFixed(1)}MB / {storage.maxMB.toFixed(0)}MB
            </span>
          </div>
          <div className="flex-1 max-w-32">
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${storage.usagePercentage > 90
                  ? 'bg-red-500'
                  : storage.usagePercentage > 75
                    ? 'bg-yellow-500'
                    : 'bg-green-500'
                  }`}
                style={{ width: `${Math.min(storage.usagePercentage, 100)}%` }}
              />
            </div>
          </div>
          <span className={`font-medium ${storage.usagePercentage > 90
            ? 'text-red-500'
            : storage.usagePercentage > 75
              ? 'text-yellow-500'
              : 'text-green-500'
            }`}>
            {storage.usagePercentage.toFixed(1)}%
          </span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-100 dark:border-gray-800">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => selectedMediaType === undefined ? setActiveTab(tab) : undefined}
            className={`
              px-4 py-3 text-sm font-medium capitalize border-b-2 transition-colors
              ${activeTab === tab
                ? 'border-[#3967fe] text-[#3967fe]'
                : 'border-transparent text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-200'}
            `}
          >
            {tab}s ({items.filter(item => item.type === tab).length})
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div className="flex-1 min-h-0 overflow-y-auto max-h-[450px] custom-scrollbar w-full pr-2">
        {isLoadingMedia ? (
          <div className="h-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3967fe]"></div>
          </div>
        ) : items.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-gray-400 gap-4 p-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
            <div className="w-16 h-16 rounded-full bg-gray-50 dark:bg-gray-800 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-300 dark:text-gray-400">
                <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" />
                <path d="M12 12v9" />
                <path d="m16 16-4-4-4 4" />
              </svg>
            </div>
            <div className="text-center">
              <p className="font-medium text-gray-600 dark:text-gray-300">No {activeTab}s found</p>
              <p className="text-sm text-gray-400 mt-1">Upload your first {activeTab} to get started</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pb-4">
            {items.map((item) => (
              <MediaCard
                key={item.id}
                item={item}
                onDelete={handleDeleteMedia}
                onClose={onClose}
                onSelect={setSelectedMedia}
                excludedExtentions={excludedExtentions}
              />
            ))}
          </div>
        )}
      </div>

      {/* PNG Conversion Prompt */}
      <Modal
        isOpen={convertPromptOpen}
        onClose={handleCancelConvert}
        title="Unsupported Format"
        size="sm"
        closeOnBackdropClick={!isConverting}
        actions={[
          {
            label: "No, cancel",
            color: "secondary",
            disabled: isConverting,
            onClick: handleCancelConvert
          },
          {
            label: isConverting ? "Converting..." : "Yes, convert to PNG",
            color: "primary",
            disabled: isConverting,
            loading: isConverting,
            onClick: handleConvertAndUpload
          }
        ]}
      >
        <div className="text-center">
          {/* Icon */}
          <div className="w-12 h-12 rounded-full bg-yellow-50 dark:bg-yellow-500/10 flex items-center justify-center mb-4 mx-auto">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-yellow-500">
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
              <path d="M12 9v4" />
              <path d="M12 17h.01" />
            </svg>
          </div>

          <p className="text-sm text-gray-500 dark:text-gray-300">
            <span className="font-medium text-gray-700 dark:text-gray-200">.{pendingFileExt}</span> is not supported.
            Would you like to convert it to <span className="font-medium text-[#3967fe]">PNG</span> and upload?
          </p>
        </div>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={deleteDialogOpen}
        title="Delete Media File"
        message={`Are you sure you want to delete "${mediaToDelete?.name}"? This action cannot be undone.`}
        confirmText={deleteMediaMutation.isPending ? 'Deleting...' : 'Delete'}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        onClose={handleCancelDelete}
      />
    </div>
  </Modal>
);
};